// PageStatsRow — a horizontal strip of 3 or 4 stat tiles, used as the
// hero metrics rhythm at the top of dashboard pages (Action Items, Focus,
// future Agents). Each tile is a GlassCard with an uppercase eyebrow
// label, a primary numeric value rendered via AnimatedCounter, optional
// secondary line, and an optional lucide icon.
//
// Loading rule: when `value` is undefined or null, render an em-dash ("—")
// instead of the AnimatedCounter. AnimatedCounter assumes a finite numeric
// value (it would spring to 0, which reads as a real "zero count" — wrong).
//
// Responsive rhythm:
//   <640px         : single column (stacked)
//   640px–768px    : 2-up grid
//   ≥768px         : full row (3-up or 4-up)
// Achieved with `grid-cols-1 sm:grid-cols-2 md:grid-cols-N`.

import type { ComponentType, SVGProps } from 'react';
import GlassCard from './GlassCard';

export type PageStat = {
  label: string;
  value: number | null | undefined;
  // AnimatedCounter formatting passthrough — these tiles default to integer
  // counts (decimals=0). Pages that need a fractional value (e.g. "3.5 hrs"
  // protected) can opt in via `decimals`.
  decimals?: number;
  prefix?: string;
  suffix?: string;
  // Optional second line under the number — used for secondary context
  // like "since 06:00 UTC" or "due ≤7d". Kept short.
  secondary?: string;
  // Optional lucide icon. We accept the lucide-react export shape
  // (a component taking SVGProps).
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  // Optional tint for the icon — falls back to text-muted-foreground.
  iconClassName?: string;
  // Optional tint for the value — falls back to text-foreground.
  valueClassName?: string;
};

type PageStatsRowProps = {
  stats: PageStat[];
  className?: string;
};

const PageStatsRow = ({ stats, className = '' }: PageStatsRowProps) => {
  // Pin grid column count from the data shape so 3-up + 4-up both lay out
  // cleanly at md+. Anything outside [3,4] falls back to grid-cols-3.
  const mdCols = stats.length === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3';
  return (
    <div
      className={`grid grid-cols-1 sm:grid-cols-2 ${mdCols} gap-4 ${className}`}
      data-testid="page-stats-row"
    >
      {stats.map((stat, i) => (
        <StatTile key={`${stat.label}-${i}`} stat={stat} />
      ))}
    </div>
  );
};

function StatTile({ stat }: { stat: PageStat }) {
  const Icon = stat.icon;
  const hasValue = typeof stat.value === 'number' && Number.isFinite(stat.value);
  return (
    <GlassCard hover={false} className="py-4">
      <div className="flex items-center gap-2 mb-2">
        {Icon && (
          <span className="neo-chip w-7 h-7 flex items-center justify-center shrink-0">
            <Icon
              className={`w-3.5 h-3.5 ${stat.iconClassName ?? 'text-forest/70'}`}
              aria-hidden="true"
            />
          </span>
        )}
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{stat.label}</p>
      </div>
      {hasValue ? (
        // Plain number render instead of AnimatedCounter. The spring spends
        // ~300ms on the way from 0 → target and renders the intermediate
        // value (e.g. "0" when actual is 1) which contradicts the secondary
        // line and reads as a bug. Phase 3 Agents page hit the same issue
        // and switched to toLocaleString — applying that lesson here.
        <span
          className={`font-mono text-2xl font-bold ${stat.valueClassName ?? 'text-foreground'}`}
          aria-label={`${stat.label}: ${stat.value}`}
        >
          {`${stat.prefix ?? ''}${(stat.value as number).toLocaleString(undefined, {
            minimumFractionDigits: stat.decimals ?? 0,
            maximumFractionDigits: stat.decimals ?? 0,
          })}${stat.suffix ?? ''}`}
        </span>
      ) : (
        <span
          className={`font-mono text-2xl font-bold ${stat.valueClassName ?? 'text-muted-foreground'}`}
          aria-label="value unavailable"
        >
          —
        </span>
      )}
      {stat.secondary && (
        <p className="mt-1 text-xs text-muted-foreground line-clamp-1">{stat.secondary}</p>
      )}
    </GlassCard>
  );
}

export default PageStatsRow;
